'use client'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { FDRHeader } from 'components/MegaMenu/components/FDRHeader'
import { STICKY_HEADER_CTA_VIEWPORT_IDS } from 'constants/header'
import { mainProductConfig } from 'constants/mainProductConfig'
import { FDR_GET_STARTED_ROUTE } from 'constants/navigation'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { useAnyElementInViewport } from 'hooks/useAnyElementInViewport'
import useMobileNavState from 'hooks/useMobileNavState'
import useScrollBelow from 'hooks/useScrollBelow'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import {
  FDR_PERSISTENT_HEADER_VARIANTS,
  getFdrPersistentHeaderVariant,
} from './fdrPersistentHeaderRoutes'

const PERSISTENT_HEADER_SCROLL_THRESHOLD = 150

const DEFAULT_APPLY_URL = `https://${FDR_GET_STARTED_ROUTE}`

/**
 * @param {boolean} slideWithMobileNav - The page uses the side mobile nav; like legacy, the header
 *   slides right with it (and toggles it) when the nav is opened from this header
 */
export default function FDRPersistentHeader({
  siteEnv,
  footerSticky,
  linkUrl,
  phoneData,
  slideWithMobileNav = false,
}) {
  const { asPath } = useAppRouter()
  const [mobileNavOpen, setMobileNavOpen] = useMobileNavState()
  const { fireAndForget } = useTriggerEvents()
  const isScrolledPastThreshold = useScrollBelow(PERSISTENT_HEADER_SCROLL_THRESHOLD)
  const ctasAreInViewport = useAnyElementInViewport(STICKY_HEADER_CTA_VIEWPORT_IDS, 0)
  const [openedMenuFromHeader, setOpenedMenuFromHeader] = useState(false)

  useEffect(() => {
    if (!mobileNavOpen) setOpenedMenuFromHeader(false)
  }, [mobileNavOpen])

  const variant = getFdrPersistentHeaderVariant(asPath)
  if (!variant) return null

  const isOpenWithHeader = slideWithMobileNav && mobileNavOpen && openedMenuFromHeader
  const isVisible =
    isOpenWithHeader || (isScrolledPastThreshold && !ctasAreInViewport && !mobileNavOpen)
  const showTrustpilot = variant === FDR_PERSISTENT_HEADER_VARIANTS.ARTICLE
  const { image, subtitle, totalRating, totalReviews } = footerSticky?.card ?? {}

  // Same tracking as the mobile header menu buttons
  const handleMenuClick = () => {
    const opening = !isOpenWithHeader
    fireAndForget({
      nav_link_section: 'Toggle menu',
      click_type: 'Button Click',
      click_id: opening ? 'open button' : 'close button',
    })
    setOpenedMenuFromHeader(opening)
    setMobileNavOpen(opening)
  }

  return (
    <FDRHeader
      siteEnv={siteEnv}
      tp_image={showTrustpilot ? image : null}
      tp_text={totalReviews ? `${totalRating}/5 from ${totalReviews} reviews` : subtitle}
      linkUrl={linkUrl || DEFAULT_APPLY_URL}
      phoneData={phoneData}
      mainProductConfig={mainProductConfig}
      backLink="/"
      ariaHidden={!isVisible}
      onMenuClick={handleMenuClick}
      menuOpen={isOpenWithHeader}
      className={classNames(
        'lg:hidden transform-gpu transition-[transform,opacity] duration-300 ease-in-out will-change-transform motion-reduce:transition-none',
        {
          'translate-x-68': isOpenWithHeader,
          'translate-y-0 opacity-100': isVisible,
          'pointer-events-none -translate-y-full opacity-0': !isVisible,
        }
      )}
    />
  )
}
